import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Link,
  Collapse
} from '@mui/material';
import { SportsTennis as TennisIcon } from '@mui/icons-material';
import { getAllUserProfiles, NTRP_LEVELS } from '../utils/firebaseUtils';

const DAY_ORDER = ['Pirmadienis', 'Antradienis', 'Trečiadienis', 'Ketvirtadienis', 'Penktadienis', 'Šeštadienis', 'Sekmadienis'];
const WEEK_DAY_NAMES_SHORT = ['Pr', 'An', 'Tr', 'Kt', 'Pn', 'Št', 'Sk'];
const TIME_SLOTS = [];
for (let h = 7; h <= 21; h++) {
  TIME_SLOTS.push(`${h.toString().padStart(2, '0')}:00`);
}

// NTRP ranges: 1.0-1.5, 1.5-2.0, ...
const NTRP_RANGES = (() => {
  const r = [];
  for (let i = 0; i < NTRP_LEVELS.length - 1; i++) {
    r.push({ value: `${NTRP_LEVELS[i]}-${NTRP_LEVELS[i + 1]}`, label: `${NTRP_LEVELS[i]}–${NTRP_LEVELS[i + 1]}`, levels: [NTRP_LEVELS[i], NTRP_LEVELS[i + 1]] });
  }
  return r;
})();

const formatWantedTimes = (preferences) => {
  if (!preferences?.length) return '—';
  const byDay = {};
  preferences.forEach((p) => {
    const day = p.dayName || p.day || '';
    const time = (p.time || '').trim();
    if (!day || !time) return;
    if (!byDay[day]) byDay[day] = [];
    byDay[day].push(time);
  });
  const lines = DAY_ORDER.filter((d) => byDay[d]).map((day) => {
    const times = (byDay[day] || []).sort();
    const timeStr = times.map((t) => `"${t}"`).join('; ');
    return `${day}: ${timeStr}`;
  });
  return lines.length > 0 ? lines.join('\n') : '—';
};

const loadGroupTrainingUsers = async () => {
  const all = await getAllUserProfiles();
  const withPreferences = all.filter(
    (u) =>
      (u.groupTrainingPreferences && u.groupTrainingPreferences.length > 0) ||
      u.groupTrainingSubmittedAt
  );
  withPreferences.sort((a, b) => {
    const ta = a.groupTrainingSubmittedAt?.toMillis?.() ?? 0;
    const tb = b.groupTrainingSubmittedAt?.toMillis?.() ?? 0;
    return tb - ta;
  });
  return withPreferences;
};

const AdminGroupFormavimasPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ntrpRange, setNtrpRange] = useState('');
  const [selectedCell, setSelectedCell] = useState(null); // { dayIndex, time }

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const list = await loadGroupTrainingUsers();
        if (!cancelled) setUsers(list);
      } catch (err) {
        console.error(err);
        if (!cancelled) setError('Nepavyko įkelti duomenų');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const rangeSpec = NTRP_RANGES.find((r) => r.value === ntrpRange);
  const filteredUsers = useMemo(() => {
    if (!rangeSpec) return users;
    const levels = new Set(rangeSpec.levels);
    return users.filter((u) => u.ntrpLevel && levels.has(u.ntrpLevel));
  }, [users, rangeSpec]);

  // For each (dayIndex, time) -> { count, users }
  const gridData = useMemo(() => {
    const map = new Map();
    filteredUsers.forEach((user) => {
      (user.groupTrainingPreferences || []).forEach((p) => {
        const dayName = p.dayName || p.day || '';
        const time = (p.time || '').trim();
        if (!dayName || !time) return;
        const dayIndex = DAY_ORDER.indexOf(dayName);
        if (dayIndex < 0) return;
        const key = `${dayIndex}-${time}`;
        if (!map.has(key)) map.set(key, { count: 0, users: [] });
        const cell = map.get(key);
        if (!cell.users.find((u) => u.userId === user.userId)) {
          cell.count += 1;
          cell.users.push(user);
        }
      });
    });
    return map;
  }, [filteredUsers]);

  const selectedCellData = selectedCell
    ? gridData.get(`${selectedCell.dayIndex}-${selectedCell.time}`)
    : null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <TennisIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
            Grupių formavimas
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <FormControl size="small" sx={{ minWidth: 180, mb: 3 }}>
          <InputLabel id="ntrp-filter-label">NTRP filtras</InputLabel>
          <Select
            labelId="ntrp-filter-label"
            label="NTRP filtras"
            value={ntrpRange}
            onChange={(e) => {
              setNtrpRange(e.target.value);
              setSelectedCell(null);
            }}
            sx={{ borderRadius: 2, bgcolor: 'background.paper' }}
          >
            <MenuItem value="">Visi lygiai</MenuItem>
            {NTRP_RANGES.map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 0.25, sm: 2, md: 3 },
            pl: { xs: 0.5, sm: 3, md: 4 },
            maxWidth: 1000,
            width: '100%',
            overflow: 'hidden',
            borderRadius: 3,
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            border: '1px solid',
            borderColor: 'divider',
            mb: 3
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Pasirinkite langelį – apačioje bus rodomi žmonės, kuriems tinka atitinkamas laikas
          </Typography>
          <Box sx={{ display: 'flex', minWidth: { xs: '100%', sm: 'max-content' } }}>
            <Box
              sx={{
                position: 'sticky',
                left: 0,
                zIndex: 5,
                bgcolor: 'background.paper',
                borderRight: '1px solid',
                borderColor: 'divider',
                minWidth: { xs: 22, sm: 50, md: 60 },
                flexShrink: 0
              }}
            >
              <Box
                sx={{
                  height: { xs: 40, sm: 52, md: 56 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'grey.50'
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.55rem', sm: '0.875rem', md: '1rem' } }}>
                  Laikas
                </Typography>
              </Box>
              {TIME_SLOTS.map((time) => (
                <Box
                  key={time}
                  sx={{
                    height: { xs: 28, sm: 36, md: 42 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: { xs: '0.55rem', sm: '0.75rem', md: '0.8rem' },
                    color: 'text.secondary',
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  {time}
                </Box>
              ))}
            </Box>
            {DAY_ORDER.map((dayName, dayIndex) => (
              <Box
                key={dayName}
                sx={{
                  minWidth: { xs: 0, sm: 80, md: 100 },
                  flex: { xs: 1, sm: 'none' },
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Box
                  sx={{
                    height: { xs: 40, sm: 52, md: 56 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'grey.50'
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    textAlign="center"
                    sx={{
                      fontSize: { xs: '0.5rem', sm: '0.75rem', md: '0.875rem' },
                      lineHeight: 1.1,
                      px: 0.5,
                      display: { xs: 'block', md: 'none' }
                    }}
                  >
                    {WEEK_DAY_NAMES_SHORT[dayIndex]}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    textAlign="center"
                    sx={{
                      fontSize: { xs: '0.5rem', sm: '0.75rem', md: '0.875rem' },
                      lineHeight: 1.1,
                      px: 0.5,
                      display: { xs: 'none', md: 'block' }
                    }}
                  >
                    {dayName}
                  </Typography>
                </Box>
                {TIME_SLOTS.map((time) => {
                  const key = `${dayIndex}-${time}`;
                  const data = gridData.get(key);
                  const count = data?.count ?? 0;
                  const isSelected = selectedCell?.dayIndex === dayIndex && selectedCell?.time === time;
                  return (
                    <Box
                      key={time}
                      onClick={() => setSelectedCell((prev) => (prev?.dayIndex === dayIndex && prev?.time === time ? null : { dayIndex, time }))}
                      sx={{
                        height: { xs: 28, sm: 36, md: 42 },
                        bgcolor: isSelected ? 'primary.main' : count > 0 ? 'primary.light' : 'background.paper',
                        color: count > 0 ? (isSelected ? 'primary.contrastText' : 'primary.contrastText') : 'text.secondary',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background-color 0.15s ease',
                        '&:hover': {
                          bgcolor: count > 0 ? (isSelected ? 'primary.dark' : 'primary.main') : 'action.hover'
                        }
                      }}
                    >
                      <Typography variant="body2" fontWeight={count > 0 ? 600 : 400}>
                        {count > 0 ? count : '·'}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>
        </Paper>

        <Collapse in={Boolean(selectedCellData)}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              {selectedCell && (
                <>
                  {DAY_ORDER[selectedCell.dayIndex]} {selectedCell.time} – {selectedCellData?.users?.length ?? 0} asm.
                </>
              )}
            </Typography>
            {selectedCellData?.users?.length ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {selectedCellData.users.map((user) => (
                  <Box
                    key={user.userId}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'grey.50'
                    }}
                  >
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {user.fullName || '—'}
                      </Typography>
                      {user.ntrpLevel && (
                        <Chip label={user.ntrpLevel} size="small" color="primary" variant="outlined" sx={{ height: 22 }} />
                      )}
                    </Box>
                    <Typography variant="body2" component="span" sx={{ display: 'block' }}>
                      {user.email ? (
                        <Link href={`mailto:${user.email}`} sx={{ fontSize: 'inherit' }}>{user.email}</Link>
                      ) : (
                        '—'
                      )}
                    </Typography>
                    <Typography variant="body2" component="span" sx={{ display: 'block' }}>
                      {user.phoneNumber ? (
                        <Link href={`tel:${user.phoneNumber}`} sx={{ fontSize: 'inherit' }}>{user.phoneNumber}</Link>
                      ) : (
                        '—'
                      )}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mt: 1, color: 'text.secondary' }}>
                      Pageidaujami laikai: {formatWantedTimes(user.groupTrainingPreferences)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography color="text.secondary">Nėra pasirinktų žmonių šiame laike.</Typography>
            )}
          </Paper>
        </Collapse>

        {filteredUsers.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            {ntrpRange
              ? 'Nėra klientų su pasirinktu NTRP intervalu.'
              : 'Nėra klientų su grupinės treniruotės pageidavimais.'}
          </Typography>
        )}
      </Container>
    </Box>
  );
};

export default AdminGroupFormavimasPage;
